import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, PackageIcon, CheckIcon, XIcon, BellIcon, ArrowRightIcon, CreditCardIcon } from 'lucide-react';
import { PremiumSelect } from './PremiumSelect';
import { Order, OrderStatus, STATUS_LABEL, seedOrders, methodOf, paymentOutstanding, money } from '../data/orders';
import { StatusChip, PaymentChip, AttentionNote } from './orders/OrderChips';
import { OrderDetailDrawer } from './orders/OrderDetailDrawer';
import { useBusiness } from './BusinessContext';
const TABS: ('all' | OrderStatus)[] = ['all', 'new', 'progress', 'fulfilled', 'cancelled'];
interface BusinessOrdersProps {
  onNavigate?: (tab: string) => void;
}

/**
 * The operational record of everything customers committed to. Written for a
 * receptionist, not an operations analyst: four statuses, payment shown
 * separately, and one obvious action per order.
 */
export function BusinessOrders({
  onNavigate
}: BusinessOrdersProps) {
  // Ordering is a business-wide setting, so it lives in context — this page
  // is only reachable once it's on, but it stays defensive.
  const {
    orderingEnabled: enabled,
    setOrderingEnabled: setEnabled
  } = useBusiness();
  const [orders, setOrders] = useState<Order[]>(seedOrders);
  const [tab, setTab] = useState<'all' | OrderStatus>('all');
  const [query, setQuery] = useState('');
  const [payment, setPayment] = useState('Any payment');
  const [date, setDate] = useState('All time');
  const [source, setSource] = useState('All sources');
  const [openId, setOpenId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState<Order | null>(null);
  const toast = (text: string) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 2400);
  };
  const counts = useMemo(() => {
    const c: Record<string, number> = {
      all: orders.length
    };
    (['new', 'progress', 'fulfilled', 'cancelled'] as OrderStatus[]).forEach(s => {
      c[s] = orders.filter(o => o.status === s).length;
    });
    return c;
  }, [orders]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter(o => {
      if (tab !== 'all' && o.status !== tab) return false;
      if (payment !== 'Any payment' && o.payment !== payment.toLowerCase()) return false;
      if (source !== 'All sources' && o.source !== source) return false;
      if (date === 'Today' && o.age > 1440) return false;
      if (date === 'Last 7 days' && o.age > 10080) return false;
      if (!q) return true;
      return o.id.toLowerCase().includes(q) || o.customer.name.toLowerCase().includes(q) || o.customer.phone.includes(q) || o.lines.some(l => l.name.toLowerCase().includes(q));
    });
  }, [orders, tab, query, payment, date, source]);
  const open = orders.find(o => o.id === openId) ?? null;
  const setStatus = (id: string, status: OrderStatus, reason?: string) => setOrders(list => list.map(o => o.id === id ? {
    ...o,
    status,
    cancelReason: reason ?? o.cancelReason,
    fulfilledBy: status === 'fulfilled' ? 'You' : o.fulfilledBy,
    fulfilledAt: status === 'fulfilled' ? '13 Sep 2026 · Just now' : o.fulfilledAt,
    timeline: [...o.timeline, {
      label: status === 'progress' ? 'Order started' : status === 'fulfilled' ? 'Fulfilled' : `Cancelled — ${(reason ?? '').toLowerCase()}`,
      time: 'Just now',
      by: 'You'
    }]
  } : o));

  /** Cash collected by hand. Recorded with who and when, and independent of
      whether the order has been fulfilled. */
  const markPaid = (id: string) => setOrders(list => list.map(o => o.id === id ? {
    ...o,
    payment: 'paid' as const,
    paidBy: 'You',
    paidAt: '13 Sep 2026 · Just now',
    timeline: [...o.timeline, {
      label: 'Cash payment recorded',
      time: 'Just now',
      by: 'You'
    }]
  } : o));

  /* ------------------------------------------------- Ordering not set up yet */
  if (!enabled) return <div className="max-w-2xl" data-id="element-713" data-mp="2850ywx36qj4">
        <Header data-id="element-714" data-mp="296ed81krp40" />
        <div className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 text-center" data-id="element-715" data-mp="1rm0gqq0fmzcq">
          <span className="w-14 h-14 rounded-2xl bg-primary-indigo/[0.06] inline-flex items-center justify-center" data-id="element-716" data-mp="1rk7a3aptkf4q">
            <CreditCardIcon className="w-6 h-6 text-primary-indigo" strokeWidth={1.75} data-id="element-717" data-mp="3h2vzcftcv51t" />
          </span>
          <h3 className="font-display font-bold text-xl text-dark-navy mt-5" data-id="element-718" data-mp="3c5m2wg7jtt4h">
            Start receiving orders
          </h3>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-md mx-auto" data-id="element-719" data-mp="3qurzdwf0558o">
            Allow customers to complete purchases or bookings through your Talab
            shop, and every order will appear here.
          </p>
          <button onClick={() => onNavigate?.('Settings')} className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-primary-indigo text-white text-sm font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-720" data-mp="3uv265bambzol">
            Set up ordering
            <ArrowRightIcon className="w-4 h-4" data-id="element-721" data-mp="2fda47lbhldxr" />
          </button>
        </div>
        <SimulateBar enabled={enabled} setEnabled={setEnabled} orders={orders} setOrders={setOrders} onNotice={setNotice} data-id="element-722" data-mp="2aep55p7z10aw" />
      </div>;
  return <div data-id="element-723" data-mp="3446dt286zk1m">
      <Header data-id="element-724" data-mp="343o5h7899fdc" />

      {/* Summary — scanning aid, not the page's centrepiece */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6" data-id="element-725" data-mp="393sob3wmym6q">
        {(['new', 'progress', 'fulfilled', 'cancelled'] as OrderStatus[]).map(s => {
        const lead = s === 'new' && counts.new > 0;
        const active = tab === s;
        return <button key={s} onClick={() => setTab(active ? 'all' : s)} className={`text-left p-4 lg:p-5 rounded-2xl border transition-colors duration-150 ease-out ${lead ? 'bg-primary-indigo border-primary-indigo' : active ? 'bg-white border-primary-indigo' : 'bg-white border-slate-200 hover:border-slate-300'}`} data-id="element-726" data-mp="3n3aennpky7i7">
                <p className={`text-xs font-semibold ${lead ? 'text-white/75' : 'text-slate-500'}`} data-id="element-727" data-mp="1v3o5ucu2rhhk">
                  {STATUS_LABEL[s]}
                </p>
                <p className={`font-display font-bold text-2xl lg:text-3xl mt-1.5 leading-none ${lead ? 'text-white' : 'text-dark-navy'}`} data-id="element-728" data-mp="1l5rx1rom0ymm">
                  {counts[s]}
                </p>
              </button>;
      })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-slate-200 mb-5" data-id="element-729" data-mp="28vm9w98xin6v">
        {TABS.map(t => {
        const active = t === tab;
        return <button key={t} onClick={() => setTab(t)} className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors duration-150 ease-out ${active ? 'text-primary-indigo' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-730" data-mp="1z5j93mvkg4xz">
              {t === 'all' ? 'All' : STATUS_LABEL[t]}
              <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-bold ${active ? 'bg-primary-indigo/[0.08] text-primary-indigo' : 'bg-slate-100 text-slate-500'}`} data-id="element-731" data-mp="204j0p2dfr45o">
                {counts[t]}
              </span>
              {active && <motion.div layoutId="orderTab" className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-indigo rounded-full" data-id="element-732" data-mp="1w5k17xah3zlm" />}
            </button>;
      })}
      </div>

      {/* Search + light filters */}
      <div className="flex flex-col lg:flex-row gap-3 mb-5" data-id="element-733" data-mp="29vnhm7mo8z35">
        <div className="relative flex-1 min-w-0" data-id="element-734" data-mp="2ev0c9ejsbaga">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-735" data-mp="2etoxykspqkax" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by customer, order number, item or phone..." className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-736" data-mp="yfyspapuxpzp" />
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-737" data-mp="zyyri0p5d8kv">
          <PremiumSelect compact value={payment} options={['Any payment', 'Paid', 'Pending', 'Failed', 'Refunded']} onChange={setPayment} data-id="element-738" data-mp="c8ctz3crr6p4" />
          <PremiumSelect compact value={date} options={['All time', 'Today', 'Last 7 days']} onChange={setDate} data-id="element-739" data-mp="h7xvnljbpt5j" />
          <PremiumSelect compact value={source} options={['All sources', 'Talab Shop', 'Website Chat', 'Talab Discover']} onChange={setSource} data-id="element-740" data-mp="3plob8layrr33" />
        </div>
      </div>

      {/* Bulk bar */}
      <AnimatePresence data-id="element-741" data-mp="1od7pdvs8by1u">
        {selected.length > 0 && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.16,
        ease: 'easeOut'
      }} className="flex items-center gap-3 px-4 py-3 mb-4 rounded-2xl bg-dark-navy text-white" data-id="element-742" data-mp="1obmv879tuh4i">
            <p className="text-sm font-semibold" data-id="element-743" data-mp="10oy733u85sfg">
              {selected.length} selected
            </p>
            <div className="flex-1" data-id="element-744" data-mp="wlid6f9ji66g" />
            <button onClick={() => {
          selected.forEach(id => setStatus(id, 'progress'));
          setSelected([]);
          toast('Marked in progress');
        }} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold hover:bg-white/20 transition-colors duration-150 ease-out" data-id="element-745" data-mp="rmyq2bj7moci">
              Mark in progress
            </button>
            <button onClick={() => {
          selected.forEach(id => setStatus(id, 'fulfilled'));
          setSelected([]);
          toast('Marked fulfilled');
        }} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold hover:bg-white/20 transition-colors duration-150 ease-out" data-id="element-746" data-mp="3h9tgv7fic5ae">
              Mark fulfilled
            </button>
            <button onClick={() => setSelected([])} aria-label="Clear selection" className="p-1.5 rounded-lg text-white/60 hover:text-white transition-colors duration-150 ease-out" data-id="element-747" data-mp="2ybqvd2m64qhc">
              <XIcon className="w-4 h-4" data-id="element-748" data-mp="25tbszvpz7azd" />
            </button>
          </motion.div>}
      </AnimatePresence>

      {visible.length === 0 ? <EmptyState filtered={orders.length > 0} onShop={() => onNavigate?.('My Shop')} data-id="element-749" data-mp="34792hw5z2w9t" /> : <>
          {/* Desktop table */}
          <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl overflow-hidden" data-id="element-750" data-mp="3hzyoi0li416x">
            <table className="w-full" data-id="element-751" data-mp="2q6ybmt4nxhon">
              <thead data-id="element-752" data-mp="2q7zjypuanq2o">
                <tr className="border-b border-slate-200" data-id="element-753" data-mp="2q7hpnyr6zlxe">
                  <Th className="w-10" data-id="element-754" data-mp="2l8n08dypyz4b" />
                  <Th data-id="element-755" data-mp="2l9pqqks4qzjp">Order</Th>
                  <Th data-id="element-756" data-mp="2z7mq1oxyxk22">Items</Th>
                  <Th data-id="element-757" data-mp="2z7wdb4rmpd42">Total</Th>
                  <Th data-id="element-758" data-mp="2z8xsnlfoglrl">Payment</Th>
                  <Th data-id="element-759" data-mp="2u8jj1yyfh3rg">Status</Th>
                  <Th data-id="element-760" data-mp="2u7i6fq1rk97m">Date</Th>
                  <Th className="text-right pr-5" data-id="element-761" data-mp="2u78e3vt8xpyr">Action</Th>
                </tr>
              </thead>
              <tbody data-id="element-762" data-mp="efklgcl9mjlr">
                {visible.map(o => <tr key={o.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors duration-150 ease-out" data-id="element-763" data-mp="9j3yvmtpsdas">
                    <td className="pl-5 py-4" data-id="element-764" data-mp="m1zgfb36z21s">
                      <button onClick={() => setSelected(s => s.includes(o.id) ? s.filter(x => x !== o.id) : [...s, o.id])} aria-label={`Select ${o.id}`} className={`w-[18px] h-[18px] rounded-md border flex items-center justify-center transition-colors duration-150 ease-out ${selected.includes(o.id) ? 'bg-primary-indigo border-primary-indigo' : 'bg-white border-slate-300 hover:border-slate-400'}`} data-id="element-765" data-mp="lylhydxnfczm">
                        {selected.includes(o.id) && <CheckIcon className="w-3 h-3 text-white" data-id="element-766" data-mp="1ofx2rc0rikfl" />}
                      </button>
                    </td>
                    <td className="py-4 pr-4 min-w-[170px]" data-id="element-767" data-mp="3w0b3p3dwdk88">
                      <p className="text-sm font-bold text-dark-navy truncate" data-id="element-768" data-mp="3rx4ze1aci34e">
                        {o.customer.name}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-769" data-mp="3n0ebirsau38f">#{o.id}</p>
                    </td>
                    <td className="py-4 pr-4 min-w-[200px]" data-id="element-770" data-mp="3i1bik5plfngt">
                      <p className="text-sm text-slate-700 truncate" data-id="element-771" data-mp="jg9w4kpmygps">
                        {o.lines[0].name}
                        {o.lines[0].variant ? ` · ${o.lines[0].variant}` : ''}
                      </p>
                      {o.lines.length > 1 && <p className="text-xs text-slate-400 mt-0.5" data-id="element-772" data-mp="4syj0edt2onk">
                          {o.lines.length} items
                        </p>}
                    </td>
                    <td className="py-4 pr-4 whitespace-nowrap" data-id="element-773" data-mp="xhmd8zgj8od9">
                      <p className="text-sm font-bold text-dark-navy" data-id="element-774" data-mp="xg1e0wj9wh92">
                        {money(o.total)}
                      </p>
                    </td>
                    <td className="py-4 pr-4" data-id="element-775" data-mp="19sn3gbie7ef9">
                      <PaymentChip payment={o.payment} data-id="element-776" data-mp="19teztojtdg6l" />
                      {methodOf(o) === 'cash' && <p className="text-[11px] font-semibold text-slate-400 mt-1" data-id="element-777" data-mp="1jprqsenz5fxn">
                          Cash / on delivery
                        </p>}
                    </td>
                    <td className="py-4 pr-4" data-id="element-778" data-mp="1sqg2v3tt567w">
                      <StatusChip status={o.status} data-id="element-779" data-mp="1sp4zv686s2hu" />
                      <div className="mt-1" data-id="element-780" data-mp="1xhg2why844em">
                        <AttentionNote kind={paymentOutstanding(o) ? 'outstanding' : o.payment === 'failed' && o.status !== 'cancelled' ? 'payment' : o.status === 'new' ? 'new' : 'none'} data-id="element-781" data-mp="1xhog6ay9dbqc" />
                      </div>
                    </td>
                    <td className="py-4 pr-4 whitespace-nowrap" data-id="element-782" data-mp="l4rbhc3j4x4f">
                      <p className="text-xs text-slate-500" data-id="element-783" data-mp="gdstm82w7qz2">{o.when}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-784" data-mp="gchp261mif6w">
                        {o.source}
                      </p>
                    </td>
                    <td className="py-4 pr-5 text-right" data-id="element-785" data-mp="q4dopljcydd8">
                      <button onClick={() => setOpenId(o.id)} className="px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-786" data-mp="q5yjnc9l3ybm">
                        View
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3" data-id="element-787" data-mp="2vrbdgtcdk5jd">
            {visible.map(o => <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-id="element-788" data-mp="2hzn2itdv3n3z">
                <div className="flex items-center justify-between gap-3" data-id="element-789" data-mp="2mx6sgqjx9obr">
                  <p className="text-xs text-slate-400" data-id="element-790" data-mp="280pfjuw341n3">#{o.id}</p>
                  <StatusChip status={o.status} data-id="element-791" data-mp="283k1jquqxuim" />
                </div>
                <p className="text-sm font-bold text-dark-navy mt-2" data-id="element-792" data-mp="2d1v91uxkjsxo">
                  {o.customer.name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-793" data-mp="1z2voozh3a07b">
                  {o.lines[0].name}
                  {o.lines.length > 1 ? ` + ${o.lines.length - 1} more` : ''}
                </p>
                <p className="font-display font-bold text-lg text-dark-navy mt-2" data-id="element-794" data-mp="3tcpk9rrp6ipq">
                  {money(o.total)}
                </p>
                <div className="flex items-center justify-between gap-3 mt-2" data-id="element-795" data-mp="1asxzlt1j7pf4">
                  <PaymentChip payment={o.payment} data-id="element-796" data-mp="15tv6n6ytt9ni" />
                  <p className="text-xs text-slate-400" data-id="element-797" data-mp="15u4xewrp3ku9">{o.when}</p>
                </div>
                <button onClick={() => setOpenId(o.id)} className="w-full mt-4 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-798" data-mp="111k4rykizdsb">
                  View order
                </button>
              </div>)}
          </div>
        </>}

      <SimulateBar enabled={enabled} setEnabled={setEnabled} orders={orders} setOrders={setOrders} onNotice={setNotice} data-id="element-799" data-mp="i390jl8dtzg5" />

      <AnimatePresence data-id="element-800" data-mp="2ci1pj1erfrz4">
        {open && <OrderDetailDrawer order={open} onClose={() => setOpenId(null)} onStatus={s => {
        setStatus(open.id, s);
        toast(s === 'progress' ? 'Order started' : s === 'fulfilled' ? 'Order marked fulfilled' : 'Order updated');
        if (s === 'fulfilled') setOpenId(null);
      }} onCancel={reason => {
        setStatus(open.id, 'cancelled', reason);
        setOpenId(null);
        toast('Order cancelled');
      }} onMarkPaid={() => {
        markPaid(open.id);
        toast('Payment recorded');
      }} data-id="element-801" data-mp="2cgqqfew9f8ec" />}
      </AnimatePresence>

      {/* New-order notification */}
      <AnimatePresence data-id="element-802" data-mp="3gi8hma5rbedm">
        {notice && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed bottom-6 right-6 z-[75] w-[320px] bg-white border border-slate-200 rounded-2xl shadow-large p-4" data-id="element-803" data-mp="3lg1l2vfodua7">
            <div className="flex items-center gap-2" data-id="element-804" data-mp="1bwlccawuagqh">
              <BellIcon className="w-4 h-4 text-primary-indigo" data-id="element-805" data-mp="1gu3pkdhw3i4a" />
              <p className="text-xs font-bold uppercase tracking-wider text-primary-indigo" data-id="element-806" data-mp="1gtln2z4mw7nx">
                New order
              </p>
              <button onClick={() => setNotice(null)} aria-label="Dismiss" className="ml-auto p-1 rounded-lg text-slate-400 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-807" data-mp="o5fw3umzlmfn">
                <XIcon className="w-3.5 h-3.5" data-id="element-808" data-mp="y5ghrn8bwen0" />
              </button>
            </div>
            <p className="text-sm font-bold text-dark-navy mt-2.5" data-id="element-809" data-mp="24j66tk8m38y9">
              #{notice.id} · {notice.customer.name}
            </p>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-810" data-mp="29kvj137klsnx">
              {notice.lines[0].name}
            </p>
            <p className="text-sm font-bold text-dark-navy mt-1.5" data-id="element-811" data-mp="3p2038g8taz7n">
              {money(notice.total)}
            </p>
            <button onClick={() => {
          setOpenId(notice.id);
          setNotice(null);
        }} className="w-full mt-3.5 py-2.5 rounded-xl bg-primary-indigo text-white text-xs font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-812" data-mp="3kaqklzlokw59">
              View order
            </button>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-813" data-mp="31dgw441i0sh5">
        {message && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2.5 bg-dark-navy text-white rounded-full text-sm font-semibold shadow-large inline-flex items-center gap-2" data-id="element-814" data-mp="31e8rbdr6oo6w">
            <CheckIcon className="w-4 h-4 text-success" data-id="element-815" data-mp="2f5tnsayp5pgt" />
            {message}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function Header() {
  return (
    // The nav tab already names the section, so this is only the one line
    // that adds meaning.
    <div className="mb-5" data-id="element-816" data-mp="3cqfxtv8oryvb">
      <p className="text-sm text-slate-500" data-id="element-817" data-mp="3cpoa16lbz5fl">
        View, process and complete customer orders.
      </p>
    </div>
  );
}
function Th({
  children,
  className = ''
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <th className={`text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 py-3 px-0 first:pl-5 ${className}`} data-id="element-818" data-mp="1meti7vkpwdn1">
      {children}
    </th>;
}
function EmptyState({
  filtered,
  onShop
}: {
  filtered: boolean;
  onShop: () => void;
}) {
  return <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-id="element-819" data-mp="4hoqv2zmkw3h">
      <span className="w-12 h-12 rounded-2xl bg-slate-50 inline-flex items-center justify-center" data-id="element-820" data-mp="4jsurkxixv3r">
        <PackageIcon className="w-5 h-5 text-slate-300" strokeWidth={1.75} data-id="element-821" data-mp="if4xaehx8pv8" />
      </span>
      <h3 className="font-display font-bold text-lg text-dark-navy mt-4" data-id="element-822" data-mp="edrqa133egmq">
        {filtered ? 'No orders match this view' : 'No orders yet'}
      </h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto leading-relaxed" data-id="element-823" data-mp="3hxdzaipdq8jq">
        {filtered ? 'Try a different status, payment state or search term.' : 'When customers complete a purchase, booking or rental, their orders will appear here.'}
      </p>
      {!filtered && <button onClick={onShop} className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-824" data-mp="336n739jvl7g3">
          Go to My Shop
          <ArrowRightIcon className="w-4 h-4" data-id="element-825" data-mp="3bx5htm4vj1xo" />
        </button>}
    </div>;
}

/** Prototype-only: see the empty, not-enabled and new-order states. */
function SimulateBar({
  enabled,
  setEnabled,
  orders,
  setOrders,
  onNotice
}: {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  onNotice: (o: Order) => void;
}) {
  return <div className="flex flex-wrap items-center gap-2 mt-8" data-id="element-826" data-mp="3fitvps3v27k">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-827" data-mp="3dy40dgqk7g1">
        Simulate
      </span>
      {[{
      label: 'Ordering on',
      on: enabled && orders.length > 0,
      run: () => {
        setEnabled(true);
        setOrders(seedOrders);
      }
    }, {
      label: 'No orders yet',
      on: enabled && orders.length === 0,
      run: () => {
        setEnabled(true);
        setOrders([]);
      }
    }].map(s => <button key={s.label} onClick={s.run} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors duration-150 ease-out ${s.on ? 'bg-slate-100 text-dark-navy' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-828" data-mp="22uu8nwywx7d0">
          {s.label}
        </button>)}
      <button onClick={() => {
      const fresh: Order = {
        ...seedOrders[0],
        id: `TLB-10${50 + orders.length}`,
        customer: {
          ...seedOrders[0].customer,
          name: 'Nadia Farouk'
        },
        status: 'new',
        when: 'Today, just now',
        age: 0
      };
      setEnabled(true);
      setOrders(list => [fresh, ...list]);
      onNotice(fresh);
    }} className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-400 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-829" data-mp="2qk502nudhpeh">
        New order arrives
      </button>
    </div>;
}